/**
 * Reduced Phase 1 — Branch ranking table.
 * Client-side aggregation over the already-loaded reports list. Shows
 * one row per branch: avg TrustX®, evaluation count, most recent visit.
 */
import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

function fmtDate(iso) {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  } catch { return s; }
}

export function BranchRanking({ reports, competitorBranchIds }) {
  const compSet = useMemo(() => new Set(competitorBranchIds || []), [competitorBranchIds]);

  const rows = useMemo(() => {
    const acc = new Map(); // branch_id -> { name, scores: [], count, latest, isComp }
    (reports || []).forEach(r => {
      const id = r.branch_id;
      if (!id) return;
      if (!acc.has(id)) {
        acc.set(id, {
          branch_id: id,
          name: r.branch_name || id,
          scores: [],
          count: 0,
          latest: '',
          isComp: compSet.has(id),
        });
      }
      const row = acc.get(id);
      row.count += 1;
      if (typeof r.trustx_overall === 'number') row.scores.push(r.trustx_overall);
      const vd = r.visit_date || '';
      if (vd > row.latest) row.latest = vd;
    });

    return Array.from(acc.values())
      .map(row => ({
        ...row,
        avg: row.scores.length
          ? Math.round((row.scores.reduce((s, v) => s + v, 0) / row.scores.length) * 10) / 10
          : null,
      }))
      .sort((a, b) => {
        // Nulls last, then higher avg first, then more reports first.
        if (a.avg == null && b.avg == null) return b.count - a.count;
        if (a.avg == null) return 1;
        if (b.avg == null) return -1;
        if (b.avg !== a.avg) return b.avg - a.avg;
        return b.count - a.count;
      });
  }, [reports, compSet]);

  if (rows.length === 0) {
    return (
      <div
        className="bg-white border border-[#E2E8F0] rounded-xl p-6 text-sm text-[#94A3B8]"
        data-testid="ms-branch-ranking-empty"
      >
        No branch-level data yet. Ranking appears once at least one published
        report is available.
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden"
      data-testid="ms-branch-ranking"
    >
      <div className="px-4 py-3 border-b border-[#F1F5F9] flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#0F172A]">Branch Ranking</h3>
        <span className="text-[11px] text-[#94A3B8]">{rows.length} branch{rows.length === 1 ? '' : 'es'}</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-[#F8FAFC]">
            <TableHead className="w-[60px]">#</TableHead>
            <TableHead>Branch</TableHead>
            <TableHead className="w-[110px]">Type</TableHead>
            <TableHead className="w-[110px]">Avg TrustX®</TableHead>
            <TableHead className="w-[100px]">Reports</TableHead>
            <TableHead className="w-[140px]">Most recent</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={row.branch_id} data-testid={`ms-branch-row-${row.branch_id}`}>
              <TableCell className="text-xs text-[#94A3B8]">{idx + 1}</TableCell>
              <TableCell className="text-sm font-medium text-[#0F172A]">{row.name}</TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={row.isComp
                    ? 'bg-amber-50 text-amber-800 border-amber-200 text-[10px]'
                    : 'bg-slate-50 text-slate-700 border-slate-200 text-[10px]'}
                >
                  {row.isComp ? 'Competitor' : 'Own'}
                </Badge>
              </TableCell>
              <TableCell className="text-sm font-semibold text-[#0F172A]">
                {row.avg == null ? '—' : `${row.avg}%`}
              </TableCell>
              <TableCell className="text-sm text-[#475569]">{row.count}</TableCell>
              <TableCell className="text-sm text-[#475569]">{fmtDate(row.latest)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default BranchRanking;
